import {
  collection,
  query,
  onSnapshot,
  type Unsubscribe,
  type QuerySnapshot,
  type DocumentChange,
  type FirestoreError,
} from 'firebase/firestore';
import { getFirebaseFirestore } from '../../config/firebase';
import { getUser } from '../auth/authApi';
import { useAuthStore } from '../../../stores/authStore';
import {
  type PaymentNotification,
  type PaymentNotificationEventType,
  VALID_SUBSCRIPTION_EVENT_TYPES,
  VALID_CANCEL_EVENT_TYPES,
  type PaymentNotificationListenerOptions,
} from '../../../types/payment';
import { logger } from '../../core/env';

class PaymentNotificationService {
  private unsubscribeFromPaymentNotifications: Unsubscribe | null = null;
  private isListening = false;
  private currentUserId: string | null = null;
  private onPaymentStatusChange: ((oldStatus: string, newStatus: string) => void) | null = null;

  public startListening(
    userId: string,
    options: PaymentNotificationListenerOptions = {},
    onPaymentStatusChange?: (oldStatus: string, newStatus: string) => void
  ): void {
    if (this.isListening && this.currentUserId === userId) {
      logger.debug('Already listening for payment notifications for user:', userId);
      return;
    }

    // Clean up existing listener
    this.stopListening();

    // Store the callback for payment status changes
    this.onPaymentStatusChange = onPaymentStatusChange || null;

    const {
      timeWindowMs = 30 * 60 * 1000, // 30 minutes default
      includeMetadataChanges = false,
      source = 'default',
    } = options;

    try {

      const db = getFirebaseFirestore();
      const currentUTCTimestamp = Date.now() - timeWindowMs;
      console.log('start listening, currentUTCTimestamp:', currentUTCTimestamp);
      logger.debug('Starting payment notification listener:', {
        userId,
        timeWindowMs,
        currentUTCTimestamp,
        includeMetadataChanges,
        source,
      });

      // Create collection reference
      const notificationCollection = collection(db, `users/${userId}/paymentNotifications`);
      const q = query(notificationCollection);

      // Set up the real-time listener
      this.unsubscribeFromPaymentNotifications = onSnapshot(
        q,
        {
          includeMetadataChanges,
          source,
        },
        (snapshot: QuerySnapshot) => {
          this.handleSnapshotChanges(snapshot, currentUTCTimestamp);
        },
        (error: FirestoreError) => {
          this.handleListenerError(error);
        }
      );

      this.isListening = true;
      this.currentUserId = userId;

      logger.info('Payment notification listener started successfully for user:', userId);
    } catch (error) {
      logger.error('Failed to start payment notification listener:', error);
      this.handleListenerError(error as FirestoreError);
    }
  }

  public stopListening(): void {
    if (this.unsubscribeFromPaymentNotifications) {
      try {
        this.unsubscribeFromPaymentNotifications();
        logger.debug('Payment notification listener stopped');
      } catch (error) {
        logger.warn('Error stopping payment notification listener:', error);
      } finally {
        this.unsubscribeFromPaymentNotifications = null;
        this.isListening = false;
        this.currentUserId = null;
      }
    }
  }

  private handleSnapshotChanges(snapshot: QuerySnapshot, currentUTCTimestamp: number): void {
    try {
      snapshot.docChanges().forEach((change: DocumentChange) => {
        if (change.type === 'added' && Number(change.doc.id) >= currentUTCTimestamp) {
          const data = change.doc.data() as PaymentNotification;

          // Check if the event type is valid for triggering user profile refresh
          if (this.isValidEventType(data.eventType)) {
            logger.info('Valid payment notification received:', {
              eventType: data.eventType,
              userId: data.userId,
              timestamp: data.timestamp,
            });

            // Refresh user profile
            setTimeout(()=>{
              this.refreshUserProfile();
            }, 1000)
          }
        }
      });
    } catch (error) {
      logger.error('Error handling snapshot changes:', error);
    }
  }

  private isValidEventType(eventType: PaymentNotificationEventType): boolean {
    return (
      VALID_SUBSCRIPTION_EVENT_TYPES.includes(eventType) ||
      VALID_CANCEL_EVENT_TYPES.includes(eventType)
    );
  }

  private async refreshUserProfile(): Promise<void> {
    try {
      // Get current user ID from auth store
      const currentUser = useAuthStore.getState().user;

      if (!currentUser?.id) {
        logger.warn('Cannot refresh user profile: no authenticated user found');
        return;
      }

      logger.debug('Refreshing user profile for payment notification:', currentUser.id);

      // Get updated user profile from API
      const updatedUser = await getUser(currentUser.id);

      // Check if payment status has changed
      const currentPaymentStatus = currentUser.payment?.paymentStatus;
      const newPaymentStatus = updatedUser.payment?.paymentStatus;

      // Update user profile in auth store
      useAuthStore.getState().updateUserFromPaymentNotification(updatedUser);

      // Not really care if it's old or new payment status, from firebase it means payment status is successfully updated
      // Just need to update the page
      logger.info('Payment status changed:', {
        userId: currentUser.id,
        from: currentPaymentStatus,
        to: newPaymentStatus,
      });

      // Call the callback if provided
      if (this.onPaymentStatusChange) {
        logger.info('Calling payment status change callback:', {
          from: currentPaymentStatus,
          to: newPaymentStatus,
        });
        this.onPaymentStatusChange(currentPaymentStatus, newPaymentStatus);
      }

      logger.info('User profile refreshed successfully for payment notification');
    } catch (error) {
      logger.error('Failed to refresh user profile for payment notification:', error);
    }
  }

  private handleListenerError(error: FirestoreError): void {
    logger.error('Payment notification listener error:', {
      code: error.code,
      message: error.message,
      userId: this.currentUserId,
    });

    // Reset state on error
    this.isListening = false;
    this.currentUserId = null;
  }

  public isCurrentlyListening(): boolean {
    return this.isListening;
  }

  public getCurrentUserId(): string | null {
    return this.currentUserId;
  }
}

export const paymentNotificationService = new PaymentNotificationService();
